import { useState, useEffect } from 'react'
import s from './PipelineByClient.scss'
import { makeReportsPostRequest, getReportsBaseUrl, makeGetRequest } from 'utils/api'
import { toUsers, getTotals } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, Checkbox } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import { FaFileExcel, FaAngleRight, FaAngleDown, FaInfoCircle } from 'react-icons/fa'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import currency from 'currency.js'
import Select from 'react-select'
import { toTeams } from 'utils/serializers'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const stages = [
  {
    value: 'won',
    label: 'Won'
  },
  {
    value: 'lost',
    label: 'Lost'
  },
  {
    value: 'closed',
    label: 'Closed'
  },
  {
    value: 'project',
    label: 'Project'
  }
]

const columns = [
  {
    title: 'Bid Due Date',
    field: 'bidDueDate',
    sorting: true,
    defaultSort: 'desc',
    width: '200px',
    render: row => timezoneDate(row.bidDueDate),
    exportTransformer: row => timezoneDate(row.bidDueDate)
  },
  {
    title: 'Bid Follow-up Date',
    field: 'bidFollowUpDate',
    sorting: true,
    width: '200px',
    render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
    exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
  },
  {
    title: 'Submission Date',
    field: 'bidSubmissionDate',
    sorting: true,
    width: '200px',
    render: row => row.bidSubmissionDate && timezoneDate(row.bidSubmissionDate),
    exportTransformer: row => row.bidSubmissionDate && timezoneDate(row.bidSubmissionDate)
  },
  {
    title: 'Won Date',
    field: 'wonOnDate',
    sorting: true,
    width: '200px',
    render: row => row.wonOnDate && timezoneDate(row.wonOnDate),
    exportTransformer: row => row.wonOnDate && timezoneDate(row.wonOnDate)
  },
  {
    title: 'Lost Date',
    field: 'lostOnDate',
    sorting: true,
    width: '200px',
    render: row => row.lostOnDate && timezoneDate(row.lostOnDate),
    exportTransformer: row => row.lostOnDate && timezoneDate(row.lostOnDate)
  },
  {
    title: 'Quote #',
    field: 'quoteNumber',
    sorting: true,
    width: '200px'
  },
  {
    title: 'Project #',
    field: 'projectNumber',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Status',
    field: 'status',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Name',
    field: 'name',
    width: '200px',
    sorting: true,
    render: row => (
      <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
        {row.name}
      </a>
    )
  },
  {
    title: 'Client(s)',
    field: 'companies',
    sorting: true,
    width: '200px',
    render: row => row.companies.join(', '),
    exportTransformer: row => row.companies.join(', ')
  },
  {
    title: 'Contact(s)',
    field: 'contacts',
    sorting: true,
    width: '400px',
    render: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    },
    exportTransformer: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    }
  },
  {
    title: 'Awarded To',
    field: 'awardedCompany',
    width: '200px',
    sorting: true
  },
  {
    title: 'Value',
    field: 'value',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    )
  },
  {
    title: 'Cost',
    field: 'cost',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.cost}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.cost })
  },
  {
    title: 'Profit',
    field: 'grossProfit',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.grossProfit}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.grossProfit })
  },
  {
    title: 'Opp Manager',
    field: 'manager',
    width: '200px',
    sorting: true
  }
]

const cookies = new Cookies()

const PipelineByClient = () => {
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [stagesFilter, setStagesFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [collapse, setCollapse] = useState('')
  const [totals, setTotals] = useState({
    totalCount: 0,
    totalValue: 0
  })
  const [onlyAwardedOpportunities, setOnlyAwardedOpportunities] = useState(false)

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!startDate || !endDate) return
      const result = await withLoadingLocks(
        makeReportsPostRequest('/v1/deals/client', {
          from: startDate,
          to: endDate,
          onlyAwardedOpportunities,
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {}),
          ...(teamFilter && teamFilter.length
            ? { teamFilter: teamFilter.map(user => user.value) }
            : {}),
          ...(stagesFilter && stagesFilter.length
            ? { extraStages: stagesFilter.map(stage => stage.value) }
            : {})
        })
      )
      setResult(result)
      setTotals(getTotals(result))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, userFilter, teamFilter, stagesFilter, onlyAwardedOpportunities])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/client/export?from=${startDate}&to=${endDate}&onlyAwardedOpportunities=${onlyAwardedOpportunities}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter
          .map(user => user.value)
          .join()}&extraStages=${stagesFilter.map(stage => stage.value).join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  const toggleCollapse = stage => {
    if (collapse !== stage) {
      setCollapse(stage)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Pipeline Reports &gt; Pipeline by Client</h6>
      <div className={s.filters}>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setStagesFilter(values)}
          options={stages}
          value={stagesFilter}
          placeholder="Include Stages"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type3" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type3">
          Select Stages you&#39;d like to include in report.
        </UncontrolledTooltip>
        <div className={s.onlyAwarded}>
          <span>
            When opportunities are awarded, <br /> show them only on the awarded client
          </span>
          <Checkbox
            size="md"
            isChecked={onlyAwardedOpportunities}
            triggerCheckbox={value => {
              setOnlyAwardedOpportunities(value.target.checked)
            }}
          />
        </div>
      </div>
      {Object.keys(result).length > 0 ? (
        <>
          <p className={s.totals}>
            <b>Total Opportunities: {totals.totalCount} &#8226; </b>
            <b>
              Total Value: {currency(totals.totalValue, { symbol: getCurrency().symbol }).format()}
            </b>
          </p>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(stage => (
              <Card className={s.card} key={stage}>
                <CardHeader
                  onClick={() => toggleCollapse(stage, result[stage].statusCategoryCode)}
                  data-event={stage}
                >
                  <span className={s.arrow}>
                    {collapse === stage ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  <a
                    href={`/companies/${result[stage].companyId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {stage}
                  </a>{' '}
                  ({result[stage].count}) &#8226; Value:{' '}
                  {currency(result[stage].totalValue, { symbol: getCurrency().symbol }).format()}{' '}
                  &#8226; Not Awarded ({result[stage].countNotAwarded}):{' '}
                  {currency(result[stage].notAwarded, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Awarded Opportunities ({result[stage].countAwarded}):{' '}
                  {currency(result[stage].awarded, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Awarded to this Client ({result[stage].countAwardedToThisClient}):{' '}
                  {currency(result[stage].awardedToThisClient, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === stage}>
                  <CardBody>
                    {collapse === stage && (
                      <MaterialTable
                        title=""
                        key={stage}
                        columns={columns}
                        data={result[stage].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[stage].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Pipeline by Client')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Pipeline by Client')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try a different time range.</p>
      )}
    </>
  )
}

export default PipelineByClient
